
import React, { FC, useEffect, useState, ReactNode } from 'react'
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import Form from 'react-bootstrap/Form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { components } from 'react-select';
import AsyncSelect from 'react-select/async';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { RevRexBankAccAddGlI } from '../../../resources/form-props';
import { RevRexBankAccAddGlValidate } from '../../../resources/form-validator';
import { GL_VP, SELECT_CUSTOM_STYLE } from '../../../resources/constants';
import SelectCustomOption from '../../../components/SelectCustomOption';
import { useBankFeedApi } from '../../../resources/hooks/api/bankFeedApiHook';
import closeBtn from '../../../assets/images/close-btn.svg';

interface Props {
    isShow: boolean;
    onHide: () => void;
    onSuccess: () => void;
    branch_id: number;
}
const AddBankAccount: FC<Props> = ({ isShow, onHide, onSuccess, branch_id }) => {

    const { fetchBankTemplates, addBankAccounts } = useBankFeedApi();
    const [showLoader, setShowLoader] = useState<boolean>(false);
    const { t } = useTranslation('components.Modal.AddBankAcc');

    const { control, register, handleSubmit, formState: { errors } } = useForm<RevRexBankAccAddGlI>({
        defaultValues: {
            branch_id,
            name: '',
            desc: '',
            number: ''
        },
        resolver: yupResolver(RevRexBankAccAddGlValidate)
    });
    const [selectCustomStyle, setSlectCustomStyle] = useState<any>(SELECT_CUSTOM_STYLE('#d1ffbd'));

    const networkOptions = async (inputValue: string) => {
        const data = await _handleFetchBankTemplates(inputValue)
        return data;
    };
    const _handleFetchBankTemplates = (searchText: string): Promise<any> => {
        return new Promise<any>(async (resolve) => {
            const params = {
                csv_type_name: 'Bank Deposit',
                with_template: 2,
                network_status: 1,
                search_text: searchText || ''
            }
            fetchBankTemplates(params, (message: string, resp: any) => {
                const data = resp.data.data.rows.map((e: any) => ({
                    label: e['name'],
                    value: e['id']
                }));
                return resolve(data);
            }, (message: string, resp: any) => {
                toast.error(message);
                return resolve([]);
            })
        });
    };

    const createNewItem = async (formValues: any) => {
        setShowLoader(true)
        const params = {
            ...formValues,
            name: formValues['name']['label'],
            number: ''+formValues['number']
        }
        addBankAccounts(params, (message: string, resp: any) => {
            setShowLoader(false);
            if ( resp?.data?.success ) {
                toast.success(message);
                onSuccess();
            } else {
                toast.error(message);
            }
        }, (message: string, resp: any) => {
            setShowLoader(false);
            toast.error(message);
        });
    };

    return (
        <Modal show={isShow} onHide={onHide} backdrop="static" animation={false} size="lg" centered>
            <div className="">
                <div className="">
                    <Form onSubmit={handleSubmit(createNewItem)}>
                        <div className="modal-header">
                            <h5 className="modal-title" id="exampleModalLabel">{t('title')}</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" onClick={onHide}><img src={closeBtn} alt="" /></button>
                        </div>
                        <div className="modal-body pb-0">
                            <div className="row mt-2">
                                <div className="col-md-6">
                                    <Form.Group controlId="formTemplate">
                                        <Form.Label>{t('bank')}<strong className='text-danger'>*</strong></Form.Label>
                                        <Controller
                                            control={control}
                                            name="name"
                                            render={({ field: { value, onChange, onBlur } }) => (
                                                <AsyncSelect
                                                    placeholder={t('bankSelect.placeholder')}
                                                    isClearable={true}
                                                    defaultOptions
                                                    loadOptions={networkOptions}
                                                    onChange={onChange}
                                                    className=""
                                                    menuPosition="fixed"
                                                    value={value}
                                                    defaultValue={value}
                                                    styles={selectCustomStyle}
                                                    components={{ Option: SelectCustomOption }}
                                                />
                                            )}
                                        />
                                        {errors.name && (
                                            <Form.Text className="text-danger">
                                                <strong>{errors.name.message as ReactNode || ''}</strong>
                                            </Form.Text>
                                        )}
                                    </Form.Group>
                                </div>
                                <div className="col-md-6">
                                    <Form.Group controlId="formAccountNumber">
                                        <Form.Label>{t('accountNumber')}<strong className='text-danger'>*</strong></Form.Label>
                                        <Form.Control
                                            type="text"
                                            {...register("number")}
                                            placeholder={t('enterLastFourDigits')}
                                        />
                                        {errors.number && (
                                            <Form.Text className="text-danger">
                                                <strong>{errors.number.message as ReactNode || ''}</strong>
                                            </Form.Text>
                                        )}
                                    </Form.Group>
                                </div>
                                <div className="col-md-12 mt-2">
                                    <Form.Group controlId="formDescription">
                                        <Form.Label>{t('description')}<strong className='text-danger'>*</strong></Form.Label>
                                        <Controller
                                            render={({ field }) => (
                                                <textarea
                                                    {...field}
                                                    className="form-control"
                                                    placeholder={t('enterDescription')}
                                                />
                                            )}
                                            control={control}
                                            name="desc"
                                        />
                                        {/* <Form.Control
                                            type="textarea"
                                            {...register("desc")}
                                            placeholder={t('enterDescription')}
                                        /> */}
                                        {errors.desc && (
                                            <Form.Text className="text-danger">
                                                <strong>{errors.desc.message as ReactNode || ''}</strong>
                                            </Form.Text>
                                        )}
                                    </Form.Group>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer mt-2">
                            <button type="button" className="btn btn-cancel" onClick={onHide}>{t('close')}</button>
                            <button type="submit" className="btn btn-primary">
                                {showLoader && (<Spinner animation="border" size="sm" />) || t('saveChanges')}
                            </button>
                        </div>
                    </Form>
                </div>
            </div>
            {/* </div> */}
        </Modal>
    )
}

export default AddBankAccount